import { useEffect, useRef } from 'react';
import Cube from './Cube';

const cubeSize = 100;

// ============================================================================
// UTILITY FUNCTION: Group cubes into clusters of 2 and 3
// ============================================================================
/**
 * Splits items into groups of 2 and 3 to avoid orphans.
 * Algorithm: Always take 2 items per group, unless exactly 3 remain (then take 3).
 * 
 * Examples:
 * - 4 items: [[a,b], [c,d]]
 * - 5 items: [[a,b], [c,d,e]]
 * - 6 items: [[a,b], [c,d], [e,f]]
 * - 7 items: [[a,b], [c,d], [e,f,g]]
 */
function groupCubes(items) {
  const groups = [];
  let i = 0;
  
  while (i < items.length) {
    const remaining = items.length - i;
    
    // If exactly 3 items remain, take all 3 (prevents orphans)
    if (remaining === 3) {
      groups.push(items.slice(i, i + 3));
      i += 3;
    } else {
      // Otherwise, take 2 items
      groups.push(items.slice(i, i + 2));
      i += 2;
    }
  }
  
  return groups;
}

// ============================================================================
// SUBCOMPONENT: CubeGroup - Atomic flex unit containing spacer + cubes
// ============================================================================
function CubeGroup({ items, rowIndex, groupStartIndex, onCubeClick, activeCubeId, dataAos, dataAosDuration, dataAosDelay, delayStepTime, globalStartIndex, dataAosAnchorPlacement }) {
  return (
    <div className="cube-group" style={{ display: 'inline-flex' }}>
      {/* Spacer always rendered, but initially hidden - visibility controlled by hook */}
      <div className="cube-spacer" style={{ display: 'none' }} />
      
      {/* Render cubes in this group */}
      {items.map((item, indexInGroup) => {
        const itemIndex = groupStartIndex + indexInGroup;
        const cubeId = `${rowIndex}-${itemIndex}`;
        const globalIndex = globalStartIndex + indexInGroup;
        const cubeDelay = dataAos
          ? parseInt(dataAosDelay, 10) + globalIndex * parseInt(delayStepTime, 10)
          : undefined;
        return (
          <Cube
            key={cubeId}
            element={item}
            rowIndex={rowIndex}
            itemIndex={itemIndex}
            onCubeClick={onCubeClick}
            isActive={activeCubeId === cubeId}
            cubeId={cubeId}
            dataAos={dataAos}
            dataAosDuration={dataAosDuration}
            dataAosDelay={cubeDelay}
            dataAosAnchorPlacement={dataAosAnchorPlacement}
          />
        );
      })}
    </div>
  );
}

// ============================================================================
// CUSTOM HOOK: Manage spacer visibility based on visual row position
// ============================================================================
/**
 * After render and on resize, determines the visual row of each cube-group
 * and applies the even/odd display rule to spacers:
 * - Even visual row: show first spacer only
 * - Odd visual row: hide all spacers
 *
 * Uses a two-pass approach to avoid measurement issues:
 * 1. Hide all spacers first (to get clean layout)
 * 2. Measure cube-groups (which are always visible)
 * 3. Show appropriate spacers based on visual row
 */
function useSpacerLayout(sceneRef) {
  useEffect(() => {
    let rafId = null;
    
    const updateSpacerVisibility = () => {
      if (!sceneRef.current) return;
      
      // Get all cube groups in the scene
      const groups = Array.from(sceneRef.current.querySelectorAll('.cube-group'));
      if (groups.length === 0) return;

      // Measure cube-group positions 
      const groupsByRow = new Map();
      const tolerance = 10; // px tolerance for grouping by Y position
      
      groups.forEach(group => {
        const rect = group.getBoundingClientRect();
        const y = Math.round(rect.top);
        
        // Find existing row with similar Y position
        let foundRow = null;
        for (const [rowY] of groupsByRow.entries()) {
          if (Math.abs(rowY - y) < tolerance) {
            foundRow = rowY;
            break;
          }
        }
        
        if (foundRow !== null) {
          groupsByRow.get(foundRow).push(group);
        } else {
          groupsByRow.set(y, [group]);
        }
      });
      
      // Sort rows by Y position and assign visual row indices
      const sortedRows = Array.from(groupsByRow.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, groups]) => groups);
      
      // PASS 3: Apply even/odd rule to each visual row
      sortedRows.forEach((rowGroups, visualRowIndex) => {
        const isEvenRow = visualRowIndex % 2 === 0;
        
        rowGroups.forEach((group, indexInRow) => {
          const spacer = group.querySelector('.cube-spacer');
          if (!spacer) return;
          
          if (isEvenRow && indexInRow === 0) {
            // Even row: show first spacer only
            spacer.style.display = 'inline-block';
          } else {
            // Odd row: hide all spacers
            // Even row: hide non-first spacers
            spacer.style.display = 'none';
          }
        });
      });
    };
    
    // Debounced update using requestAnimationFrame to prevent oscillation
    const scheduleUpdate = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updateSpacerVisibility();
        rafId = null;
      });
    };
    
    // Initial update
    scheduleUpdate();
    
    // Set up ResizeObserver to handle viewport changes
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    
    if (sceneRef.current) {
      resizeObserver.observe(sceneRef.current);
    }
    
    // Cleanup
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [sceneRef]);
}

// ============================================================================
// MAIN COMPONENT: CubeLayout
// ============================================================================
export default function CubeLayout({
  layoutData,
  infoMap,
  onCubeClick,
  activeCubeId,
  dataAos = "zoom-in-up",
  dataAosDuration = "600",
  dataAosDelay = "0",
  delayStepTime = "100",
  dataAosAnchorPlacement = "top-bottom",
}) {
  const sceneRef = useRef(null);
  
  useEffect(() => {
    document.documentElement.style.setProperty('--cube-size', `${cubeSize}px`);
  }, []);
  
  // Apply spacer layout logic
  useSpacerLayout(sceneRef);

  return (
    <div className="scene" ref={sceneRef}>
      {layoutData.reduce((acc, row, rowIndex) => {
        // Running total of all cubes in previous rows
        const rowGlobalStart = layoutData
          .slice(0, rowIndex)
          .reduce((sum, r) => sum + r.items.length, 0);

        // Group the row's items into clusters of 2 and 3
        const groups = groupCubes(row.items);

        acc.push(
          <div
            key={rowIndex}
            className="row-wrapper"
            style={{ flexWrap: 'wrap' }}
          >
            {groups.map((groupItems, groupIndex) => {
              // Calculate starting index for cubes in this group (within the row)
              const groupStartIndex = groups
                .slice(0, groupIndex)
                .reduce((sum, g) => sum + g.length, 0);

              // Global start index for this group (across all rows)
              const globalStartIndex = rowGlobalStart + groupStartIndex;

              const splicedIconData = groupItems.map((item) => {
                    const info = (item.info_key && infoMap?.[item.info_key]) || infoMap?.default || {};
                    const iconFromInfo = info.icon_filename ?? null;
                    return { ...item, icon: item.icon ?? iconFromInfo };
                  })
              return (
                <CubeGroup
                  key={`${rowIndex}-${groupIndex}`}
                  items={splicedIconData}
                  rowIndex={rowIndex}
                  groupStartIndex={groupStartIndex}
                  onCubeClick={onCubeClick}
                  activeCubeId={activeCubeId}
                  dataAos={dataAos}
                  dataAosDuration={dataAosDuration}
                  dataAosDelay={dataAosDelay}
                  delayStepTime={delayStepTime}
                  globalStartIndex={globalStartIndex}
                  dataAosAnchorPlacement={dataAosAnchorPlacement}
                />
              );
            })}
          </div>
        );

        return acc;
      }, [])}
      </div>
    );
}
